import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Clock,
  Star,
  Building2,
  Handshake,
  User,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import Seo from "@/components/Seo";
import FaqBlock from "@/components/FaqBlock";
import EnquiryForm from "@/components/EnquiryForm";
import FinalCTA from "@/sections/FinalCTA";
import { SITE } from "@/lib/site";
import { ENTITIES, faqPageSchema } from "@/lib/entity-content";

const ICON = { "pvt-ltd": Building2, llp: Handshake, opc: User };

const ENTITY_SEO = {
  "pvt-ltd": {
    title: "Pvt Ltd Registration in India for Startup Founders",
    description:
      "Register a Private Limited Company in India. Founder-led incorporation with clarity on MOA, AOA, cap-table and DPIIT-readiness for startups.",
  },
  llp: {
    title: "LLP Registration in India for Consulting & Agency Firms",
    description:
      "Register an LLP in India. Partnership flexibility with limited liability for consulting, agency and professional firms not raising VC capital.",
  },
  opc: {
    title: "OPC Registration in India for Solo Founders",
    description:
      "Register a One Person Company in India. Solo founder? Get limited liability and a corporate identity without needing a co-founder.",
  },
};

/**
 * Shared, keyword-optimised landing page rendered at:
 *   /pvt-ltd-registration-india
 *   /llp-registration-india
 *   /opc-registration-india
 *
 * Every string is server-friendly (no lazy loading of body copy) and includes
 * ProfessionalService + FAQPage JSON-LD schemas via <Seo>.
 */
export default function EntityLandingPage({ entitySlug: fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const entity = ENTITIES[slug];

  if (!entity) {
    return (
      <div className="max-w-[900px] mx-auto px-5 py-32 text-center">
        <Seo title="Page not found — HG Corporates" description="This page could not be found." path="/" noindex />
        <h1 className="font-display text-4xl font-black">Page not found</h1>
        <Link to="/services" className="mt-6 inline-block font-bold link-underline">
          Back to services
        </Link>
      </div>
    );
  }

  const Icon = ICON[entity.slug] || Building2;
  const waLink = `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(
    `Hi Bhavik, I want help with ${entity.longName} registration in India. Can we talk?`,
  )}`;

  return (
    <div data-testid={`page-entity-${entity.slug}`}>
      <Seo
        title={ENTITY_SEO[entity.slug].title}
        description={ENTITY_SEO[entity.slug].description}
        path={entity.urlPath}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: `${entity.longName} Registration in India`,
            description: entity.heroSubline,
            provider: {
              "@type": "Organization",
              name: "HG Corporates",
              url: "https://hgcorporates.com/",
            },
            areaServed: { "@type": "Country", name: "India" },
            serviceType: `${entity.longName} Registration`,
          },
          faqPageSchema(entity.faqs),
        ]}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: `${entity.longName} Registration`, path: entity.urlPath },
        ]}
      />

      {/* Hero */}
      <section className="bg-white border-b border-[#EAEAEA]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[#EAEAEA] bg-white rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FDD805]" />
              {entity.longName} · India
            </div>
            <h1 className="mt-6 font-display text-[36px] md:text-[54px] leading-[1.05] font-black tracking-tight text-[#0F1115]">
              {entity.heroHeadline}
            </h1>
            <p className="mt-5 text-[16px] md:text-[19px] leading-relaxed text-[#1C1F26] max-w-2xl">
              {entity.heroSubline}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="bg-[#25D366] text-white rounded-full px-7 py-3.5 font-bold text-[15px] inline-flex items-center gap-2 hover:bg-[#1EBE57] transition-colors"
                data-testid="entity-whatsapp-cta"
              >
                <WhatsAppIcon size={18} /> Speak with Bhavik
              </a>
              <Link
                to="/contact"
                className="bg-white text-[#0F1115] border-2 border-[#0F1115] rounded-full px-7 py-3 font-bold text-[15px] inline-flex items-center gap-2 hover:bg-[#0F1115] hover:text-white transition-colors"
                data-testid="entity-enquiry-cta"
              >
                Submit Enquiry <ArrowRight size={16} />
              </Link>
              <Link
                to="/entity-advisor"
                className="text-[13px] font-bold text-[#0F1115] link-underline inline-flex items-center gap-1 py-2"
                data-testid="entity-advisor-link"
              >
                Not sure? Try the free 60-sec advisor
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-6 text-[13px] text-[#4A4A4A]">
              <a
                href={SITE.googleReviewsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 hover:opacity-80"
              >
                <div className="flex text-[#FDD805]">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={14} fill="#FDD805" strokeWidth={0} />
                  ))}
                </div>
                <span className="font-bold text-[#0F1115]">4.9</span> on 251 Google Reviews
              </a>
              <div className="inline-flex items-center gap-2">
                <Clock size={14} className="text-[#0F1115]" />
                <span>{entity.timeline}</span>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={120}>
            <div className="relative">
              <div className="absolute -top-5 -right-5 w-36 h-36 rounded-full bg-[#FDD805] opacity-90 -z-0" aria-hidden />
              <div className="absolute -bottom-5 -left-5 w-24 h-24 rounded-2xl border-2 border-[#0F1115] -z-0" aria-hidden />
              <div className="relative bg-white border border-[#EAEAEA] rounded-3xl p-7 shadow-[0_10px_40px_rgba(0,0,0,0.05)] z-10">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#FEF6C7] flex items-center justify-center">
                    <Icon size={26} className="text-[#0F1115]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
                      Structure
                    </div>
                    <div className="font-display font-black text-[22px] text-[#0F1115]">
                      {entity.shortName}
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-[#EAEAEA]">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A] mb-2">
                    Ideal for
                  </div>
                  <ul className="space-y-2">
                    {entity.idealFor.slice(0, 4).map((i) => (
                      <li key={i} className="flex items-start gap-2 text-[13.5px] text-[#0F1115]">
                        <CheckCircle2 size={15} className="text-[#FDD805] mt-0.5 shrink-0" />
                        <span>{i}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Details */}
      <section className="bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-display text-[28px] md:text-[36px] font-black text-[#0F1115]">
                What's included in your {entity.shortName} incorporation
              </h2>
              <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                {entity.inclusions.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-[15px] text-[#0F1115]">
                    <CheckCircle2 size={18} className="text-[#FDD805] mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={80}>
              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-[#EAEAEA] rounded-2xl p-6">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-[#4A4A4A]">
                    <FileText size={14} /> Required documents
                  </div>
                  <ul className="mt-4 space-y-2 text-[15px]">
                    {entity.requiredDocs.map((d) => (
                      <li key={d} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FDD805] mt-2.5 shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white border border-[#EAEAEA] rounded-2xl p-6">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-[#4A4A4A]">
                    <Clock size={14} /> Post-incorporation compliance
                  </div>
                  <ul className="mt-4 space-y-2 text-[15px]">
                    {entity.postCompliance.map((d) => (
                      <li key={d} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FDD805] mt-2.5 shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-4">
            <div className="sticky top-24">
              <EnquiryForm compact sourcePage={`landing:${entity.slug}`} />
            </div>
          </div>
        </div>
      </section>

      <FaqBlock title={`${entity.shortName} Registration in India — FAQs`} faqs={entity.faqs} />

      <FinalCTA />
    </div>
  );
}
